/**
 * Single owner of Tone.js's context redirect.
 *
 * Tone manages its own `AudioContext` internally. Without this
 * redirect, Tone-backed voices (granular, future synths) would
 * play into a context that isn't routed through our FX bus. The
 * engine boot calls `registerToneContext(ctx)` once after
 * `ensureContext` returns; subsequent calls are no-ops so hot
 * reloads / re-mounts stay quiet.
 *
 * The offline render path also needs Tone to see the offline
 * context. `Tone.Offline(...)` handles this automatically when
 * it's the caller, but direct `OfflineAudioContext` users must
 * explicitly register. Exposed as a named function so the offline
 * helper can opt in.
 */

import { getContext, setContext } from 'tone'

let registeredCtx: BaseAudioContext | null = null

export function registerToneContext(ctx: BaseAudioContext): void {
  if (registeredCtx === ctx) return
  try {
    const current = getContext()
    if (current.rawContext === ctx) {
      registeredCtx = ctx
      return
    }
    setContext(ctx as AudioContext)
    registeredCtx = ctx
  } catch {
    /* Tone not initialised — setContext will run lazily via the
       next import-time setup. Keep track so re-entry retries. */
    registeredCtx = null
  }
}

/** Read-only accessor for tests / debugging. Returns `null` when
 *  the redirect hasn't run yet. */
export function getRegisteredToneContext(): BaseAudioContext | null {
  return registeredCtx
}
